import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

/**
 * Owner Console shell state — sidebar collapsed + group-expansion preferences.
 * Persisted in localStorage under a single key so it survives reloads and
 * shares state between Sidebar (renders groups) and TopBar (renders collapse
 * indicator).
 */
const STORAGE_KEY = "hbf.owner.shell.v1";

const defaultState = {
  collapsed: false,
  groupsCollapsed: {},   // groupId -> true if collapsed
};

const ShellCtx = createContext(null);

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState;
    const parsed = JSON.parse(raw);
    return { ...defaultState, ...parsed };
  } catch { return defaultState; }
}

export function OwnerShellProvider({ children }) {
  const [state, setState] = useState(loadState);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* quota */ }
  }, [state]);

  const toggleCollapsed  = useCallback(() => setState(s => ({ ...s, collapsed: !s.collapsed })), []);
  const toggleGroup      = useCallback((id) => setState(s => ({
    ...s,
    groupsCollapsed: { ...s.groupsCollapsed, [id]: !s.groupsCollapsed[id] },
  })), []);
  const expandGroup      = useCallback((id) => setState(s => ({
    ...s,
    groupsCollapsed: { ...s.groupsCollapsed, [id]: false },
  })), []);

  const value = {
    collapsed:        state.collapsed,
    groupsCollapsed:  state.groupsCollapsed,
    toggleCollapsed,
    toggleGroup,
    expandGroup,
  };
  return <ShellCtx.Provider value={value}>{children}</ShellCtx.Provider>;
}

export function useOwnerShell() {
  const ctx = useContext(ShellCtx);
  if (!ctx) throw new Error("useOwnerShell must be used inside <OwnerShellProvider>");
  return ctx;
}
